import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { accessToken } from "./auth";

// Page views and clicks for the console's Visitors page (backend: modules/analytics).
//
// The visitor id is a random value kept in localStorage — one per browser, nothing derived
// from the person — and the session id one per tab, renewed after 30 idle minutes. The IP,
// its place and the device are read by the server from the request itself.

const ENDPOINT =
  (import.meta.env.VITE_API_BASE_URL ?? import.meta.env.NEXT_PUBLIC_API_BASE_URL ?? "/api/v1") +
  "/analytics/collect";
const VISITOR_KEY = "30min.visitor";
const SESSION_KEY = "30min.visit";
const IDLE_MS = 30 * 60_000;

const disabled =
  import.meta.env.MODE === "test" || (typeof navigator !== "undefined" && navigator.webdriver);

function randomId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
}

function visitorId(): string {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return "no-storage";
  }
}

function sessionId(): string {
  try {
    const now = Date.now();
    const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? "null") as
      | { id: string; at: number }
      | null;
    const id = saved && now - saved.at < IDLE_MS ? saved.id : randomId();
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ id, at: now }));
    return id;
  } catch {
    return "";
  }
}

type Beacon = { kind: "pageview" | "click" | "event"; name?: string; target?: string };

let firstPage = true;

function send(beacon: Beacon) {
  if (disabled) return;
  const body = JSON.stringify({
    ...beacon,
    visitor_id: visitorId(),
    session_id: sessionId(),
    host: location.host,
    path: location.pathname,
    search: location.search,
    title: document.title,
    // The outside page that brought them, only on the page they landed on.
    referrer: beacon.kind === "pageview" && firstPage ? document.referrer : "",
    screen: `${screen.width}x${screen.height}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  if (beacon.kind === "pageview") firstPage = false;
  const token = accessToken();
  try {
    void fetch(ENDPOINT, {
      method: "POST",
      body,
      keepalive: true,
      credentials: "include",
      // text/plain keeps a cross-origin dev server free of a preflight; the server reads JSON
      // whatever the type says.
      headers: { "Content-Type": "text/plain", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    }).catch(() => undefined);
  } catch {
    // Never let analytics break a page.
  }
}

/** Records a named event, e.g. trackEvent("order_started"). */
export function trackEvent(name: string, target = "") {
  send({ kind: "event", name, target });
}

function labelOf(el: HTMLElement): string {
  const text =
    el.getAttribute("aria-label") ||
    el.getAttribute("title") ||
    (el as HTMLInputElement).value ||
    el.innerText ||
    el.getAttribute("alt") ||
    "";
  return text.replace(/\s+/g, " ").trim().slice(0, 120);
}

function onClick(e: MouseEvent) {
  const el = (e.target as HTMLElement | null)?.closest<HTMLElement>(
    "a, button, [role=button], [role=tab], summary, input[type=submit], input[type=button], [data-track]",
  );
  if (!el) return;
  // Never the content of a field: only what a control says on its face.
  const name = el.dataset.track || labelOf(el);
  const href = el instanceof HTMLAnchorElement ? el.href : "";
  if (!name && !href) return;
  send({ kind: "click", name: name || href, target: href });
}

/** Mounted once at the root: a page view per route, and every click on a control. */
export function PageTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    send({ kind: "pageview" });
  }, [pathname]);
  useEffect(() => {
    if (disabled) return;
    document.addEventListener("click", onClick, { capture: true, passive: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);
  return null;
}
